"use client";

/**
 * Editor bridge. Only activates when the page is loaded with `?_editor=1`
 * (which the admin editor appends when populating its preview iframe).
 *
 * Two modes, controlled by the parent editor via an `editor:set-mode` message:
 *
 *  - "inline" (default): click-to-edit. Hovering a piece of text outlines it;
 *    clicking it makes it editable in place. On commit it posts an
 *    `editor:text-commit` message and the editor saves the change straight to
 *    Firestore (instant, no AI, no rebuild). Clicking an image posts
 *    `editor:image-pick` so the editor can open the media picker.
 *
 *  - "ask": the original behaviour. Clicking anything posts an `editor:pick`
 *    that the editor turns into a chat instruction for the AI agent.
 *
 * No-ops on production page loads (no editor query param, no parent window).
 */
import { useEffect } from "react";

type Mode = "inline" | "ask";

const TEXT_TAGS = new Set([
  "H1", "H2", "H3", "H4", "H5", "H6",
  "P", "SPAN", "A", "BUTTON", "LI", "TD", "TH", "LABEL", "STRONG", "EM", "DIV", "FIGCAPTION", "BLOCKQUOTE",
]);

export function EditorBridge() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("_editor") !== "1") return;
    if (window.parent === window) return; // not in an iframe

    let mode: Mode = "inline";
    let hovered: HTMLElement | null = null;
    // The last element processed by onMove — lets us skip the (somewhat costly)
    // hover/banner recompute while the pointer moves within the same element.
    let lastMoveTarget: HTMLElement | null = null;
    let editing: HTMLElement | null = null;
    let editingOriginal = "";
    // The element whose edit is awaiting the parent's Save/Cancel confirmation.
    let pendingEl: HTMLElement | null = null;
    let pendingOriginal = "";
    // The admin editor's origin, learned from its first message. Until then we
    // only ever post the non-sensitive "ready" handshake.
    let parentOrigin: string | null = null;

    const post = (msg: Record<string, unknown>) =>
      window.parent.postMessage(msg, parentOrigin || "*");

    const localeOf = (): string => {
      const seg = window.location.pathname.split("/").filter(Boolean)[0];
      // Locales include the 3-letter "pap" (Papiamentu), so allow 2–3 letters.
      return /^[a-z]{2,3}$/.test(seg || "") ? seg : "en";
    };

    // Which page region a node lives in, so the editor can disambiguate strings
    // that repeat across nav, footer and breadcrumbs (e.g. "Contact").
    const regionOf = (el: HTMLElement): string => {
      if (el.closest('[aria-label="Breadcrumb" i], nav[aria-label*="readcrumb" i]')) return "breadcrumb";
      if (el.closest("footer")) return "footer";
      if (el.closest("header, nav")) return "nav";
      return "main";
    };

    function clearHover() {
      if (hovered && hovered !== editing) {
        hovered.style.outline = "";
        hovered.style.outlineOffset = "";
        hovered.style.cursor = "";
      }
      hovered = null;
    }

    // Floating "Edit" / "Change photo" button shown on hover, so editing is a
    // deliberate action (a stray click never starts an edit).
    let actionTarget: HTMLElement | null = null;
    // For media (image/video/background) the src + kind to change.
    let actionMedia: { kind: "image" | "video" | "bg"; src: string } | null = null;
    let hideTimer: number | null = null;
    const editBtn = document.createElement("button");
    editBtn.setAttribute("type", "button");
    editBtn.style.cssText = [
      "position:fixed",
      "z-index:2147483647",
      "display:none",
      "align-items:center",
      "padding:5px 11px",
      "font:600 12px/1.2 -apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif",
      "color:#fff",
      "border:none",
      "border-radius:9999px",
      "box-shadow:0 2px 10px rgba(0,0,0,0.3)",
      "cursor:pointer",
      "white-space:nowrap",
    ].join(";");
    document.body.appendChild(editBtn);

    function hideEditButton() {
      editBtn.style.display = "none";
      actionTarget = null;
      actionMedia = null;
    }
    function scheduleHide() {
      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = window.setTimeout(hideEditButton, 600);
    }
    function cancelHide() {
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
    }
    // Position the floating button just inside the anchor's top-left corner so
    // there's no dead gap to cross (which was hiding it too soon).
    function showEditButton(anchor: HTMLElement, label: string, bg: string) {
      editBtn.textContent = label;
      editBtn.style.background = bg;
      const r = anchor.getBoundingClientRect();
      editBtn.style.top = `${Math.max(4, r.top + 2)}px`;
      editBtn.style.left = `${Math.min(window.innerWidth - 130, Math.max(4, r.left + 2))}px`;
      editBtn.style.display = "inline-flex";
    }

    editBtn.addEventListener("mouseenter", cancelHide);
    editBtn.addEventListener("mouseleave", scheduleHide);
    editBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const el = actionTarget;
      const media = actionMedia;
      hideEditButton();
      if (media && media.src) {
        post({
          type: "editor:image-pick",
          src: media.src,
          kind: media.kind,
          url: window.location.href,
          region: el ? regionOf(el) : "",
          nearestHeading: el ? nearestHeading(el) : "",
        });
        return;
      }
      if (el) {
        clearHover();
        startInlineEdit(el);
      }
    });

    // Persistent "Change banner" button for hero/header sections whose image or
    // video is full-bleed BEHIND the text — the cursor lands on the headline
    // (which shows "Edit"), so the background is otherwise unreachable. This
    // button shows in the hero's top-right whenever the cursor is anywhere in it.
    let bannerMedia: { kind: "image" | "video" | "bg"; src: string } | null = null;
    let bannerSection: HTMLElement | null = null;
    const bannerBtn = document.createElement("button");
    bannerBtn.setAttribute("type", "button");
    bannerBtn.style.cssText = editBtn.style.cssText.replace("display:none", "display:none") + ";background:#1A619F";
    document.body.appendChild(bannerBtn);

    function hideBannerButton() {
      bannerBtn.style.display = "none";
      bannerMedia = null;
      bannerSection = null;
    }
    bannerBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const media = bannerMedia;
      const sec = bannerSection;
      hideBannerButton();
      if (media && media.src) {
        post({
          type: "editor:image-pick",
          src: media.src,
          kind: media.kind,
          url: window.location.href,
          region: sec ? regionOf(sec) : "",
          nearestHeading: sec ? nearestHeading(sec) : "",
        });
      }
    });

    // Full-bleed media that fills a container behind its text — a Next.js
    // <Image fill> (renders position:absolute), a full-bleed <video>, or a CSS
    // background-image URL. A plain gradient (no url()) returns null, so
    // gradient-only heroes correctly have nothing to change.
    // A real banner is big — not a thumbnail. Card images also use <Image fill>
    // (position:absolute), so size is what separates a hero background from a
    // small content thumbnail.
    const isBanner = (el: HTMLElement): boolean => {
      const r = el.getBoundingClientRect();
      return r.width >= 480 && r.height >= 200;
    };
    function fullBleedMedia(c: HTMLElement): { kind: "image" | "video" | "bg"; src: string } | null {
      if (!isBanner(c)) return null; // skip small containers (cards, list rows)
      // Only a DIRECT-child media element is this container's own background.
      // (A plain descendant query would match the page hero from any ancestor
      // like <main>, putting "Change banner" on everything.)
      const v = c.querySelector(":scope > video");
      if (v && getComputedStyle(v).position === "absolute" && isBanner(v as HTMLElement)) {
        const src = (v as HTMLVideoElement).currentSrc || v.querySelector("source")?.src || "";
        if (src) return { kind: "video", src };
      }
      const img = c.querySelector(":scope > img") as HTMLImageElement | null;
      if (img && getComputedStyle(img).position === "absolute" && isBanner(img)) {
        const src = img.currentSrc || img.src;
        if (src) return { kind: "image", src };
      }
      const bg = urlFromBg(getComputedStyle(c).backgroundImage);
      if (bg) return { kind: "bg", src: bg };
      return null;
    }

    // The hero/header background that contains `el`, if any — the nearest
    // ancestor whose own background is full-bleed media. Skips nav/footer chrome.
    function heroMediaOf(el: HTMLElement): { section: HTMLElement; media: { kind: "image" | "video" | "bg"; src: string } } | null {
      let cur: HTMLElement | null = el;
      for (let i = 0; i < 6 && cur && cur.tagName !== "BODY"; i++, cur = cur.parentElement) {
        if (cur.tagName === "NAV" || cur.tagName === "HEADER" || cur.tagName === "FOOTER") return null;
        const m = fullBleedMedia(cur);
        if (m) return { section: cur, media: m };
      }
      return null;
    }

    function updateBanner(el: HTMLElement) {
      if (mode !== "inline" || editing) {
        hideBannerButton();
        return;
      }
      // If the cursor is directly on an image/video, that's a "Change photo"
      // target (handled on hover) — not a banner-behind-text situation.
      if (el.tagName === "IMG" || el.tagName === "VIDEO") {
        hideBannerButton();
        return;
      }
      const hero = heroMediaOf(el);
      if (!hero) {
        hideBannerButton();
        return;
      }
      bannerSection = hero.section;
      bannerMedia = hero.media;
      bannerBtn.textContent = hero.media.kind === "video" ? "Change banner video" : "Change banner photo";
      const r = hero.section.getBoundingClientRect();
      bannerBtn.style.top = `${Math.max(4, r.top + 8)}px`;
      bannerBtn.style.left = `${Math.min(window.innerWidth - 180, Math.max(4, r.right - 180))}px`;
      bannerBtn.style.display = "inline-flex";
    }

    // A leaf text element: holds text and no child elements, so it maps cleanly
    // to a single message string.
    function isTextLeaf(el: HTMLElement): boolean {
      return (
        TEXT_TAGS.has(el.tagName) &&
        el.children.length === 0 &&
        (el.textContent || "").trim().length > 0
      );
    }

    function urlFromBg(value: string): string | null {
      const m = value && value.match(/url\(["']?(.*?)["']?\)/);
      return m && m[1] && !m[1].startsWith("data:") ? m[1] : null;
    }

    // The media directly under the cursor: an image/video the pointer is on, or
    // an element with a CSS background-image. Precise on purpose — it does NOT
    // climb around looking for nearby images (that caused the wrong photo / nav
    // logo / page image to be picked). Full-bleed hero backgrounds that sit
    // behind text are handled separately by the "Change banner" button.
    function findMedia(el: HTMLElement): { kind: "image" | "video" | "bg"; src: string } | null {
      if (el.tagName === "IMG") {
        return { kind: "image", src: (el as HTMLImageElement).currentSrc || (el as HTMLImageElement).src };
      }
      if (el.tagName === "VIDEO") {
        const v = el as HTMLVideoElement;
        return { kind: "video", src: v.currentSrc || v.querySelector("source")?.src || "" };
      }
      // CSS background-image on the element or a near ancestor (real url(), not a
      // gradient). Stop before nav/header/footer chrome.
      let cur: HTMLElement | null = el;
      for (let i = 0; i < 3 && cur; i++, cur = cur.parentElement) {
        if (cur.tagName === "NAV" || cur.tagName === "HEADER" || cur.tagName === "FOOTER") break;
        const bg = urlFromBg(getComputedStyle(cur).backgroundImage);
        if (bg) return { kind: "bg", src: bg };
      }
      return null;
    }

    function onMove(e: MouseEvent) {
      if (editing) return;
      const t = e.target as HTMLElement | null;
      if (!t || t === editBtn || t === bannerBtn) return; // keep buttons visible
      if (t === lastMoveTarget) return; // only recompute when the element changes
      lastMoveTarget = t;

      // Always keep the hero "Change banner" affordance in sync with the cursor.
      updateBanner(t);

      if (mode === "ask") {
        if (t === hovered) return;
        clearHover();
        hovered = t;
        hovered.style.outline = "2px dashed rgba(245,166,35,0.95)";
        hovered.style.outlineOffset = "2px";
        hovered.style.cursor = "pointer";
        return;
      }

      // inline mode: text gets an "Edit" button; images/videos/backgrounds get a
      // "Change …" button. Editing only starts when the user presses the button.
      if (isTextLeaf(t)) {
        if (t === hovered) return;
        cancelHide();
        clearHover();
        hovered = t;
        actionTarget = t;
        actionMedia = null;
        hovered.style.outline = "2px dashed rgba(245,166,35,0.95)";
        hovered.style.outlineOffset = "2px";
        showEditButton(t, "Edit", "#F5A623");
        return;
      }

      const media = findMedia(t);
      if (!media || !media.src) {
        scheduleHide();
        return;
      }
      if (t === hovered) return;
      cancelHide();
      clearHover();
      hovered = t;
      actionTarget = t;
      actionMedia = media;
      hovered.style.outline = "2px dashed rgba(74,191,207,0.95)";
      hovered.style.outlineOffset = "2px";
      const label = media.kind === "video" ? "Change video" : media.kind === "bg" ? "Change background" : "Change photo";
      showEditButton(t, label, "#1A619F");
    }

    function nearestHeading(el: HTMLElement): string {
      let cur: HTMLElement | null = el;
      while (cur) {
        const h = cur.querySelector("h1, h2, h3, h4");
        if (h?.textContent) return h.textContent.trim().slice(0, 120);
        const sibH = cur.parentElement?.querySelector("h1, h2, h3, h4");
        if (sibH?.textContent) return sibH.textContent.trim().slice(0, 120);
        cur = cur.parentElement;
        if (cur && (cur.tagName === "MAIN" || cur.tagName === "BODY")) break;
      }
      return "";
    }

    function commitEdit() {
      if (!editing) return;
      const el = editing;
      const next = (el.textContent || "").trim();
      el.removeAttribute("contenteditable");
      el.style.outline = "";
      el.style.outlineOffset = "";
      el.style.cursor = "";
      const original = editingOriginal;
      editing = null;
      editingOriginal = "";
      if (next && next !== original) {
        // Hold a reference so a "Cancel" in the parent's confirm dialog can put
        // the original text back. Nothing is saved until the parent confirms.
        pendingEl = el;
        pendingOriginal = original;
        post({
          type: "editor:text-commit",
          originalText: original,
          newText: next,
          url: window.location.href,
          locale: localeOf(),
          region: regionOf(el),
          nearestHeading: nearestHeading(el),
        });
      }
    }

    function cancelEdit() {
      if (!editing) return;
      editing.textContent = editingOriginal;
      editing.removeAttribute("contenteditable");
      editing.style.outline = "";
      editing.style.cursor = "";
      editing = null;
      editingOriginal = "";
    }

    function startInlineEdit(el: HTMLElement) {
      hideBannerButton();
      editing = el;
      editingOriginal = (el.textContent || "").trim();
      el.setAttribute("contenteditable", "plaintext-only");
      el.style.outline = "2px solid rgba(245,166,35,1)";
      el.style.outlineOffset = "2px";
      el.focus();
      // Select the whole text so typing replaces it.
      const range = document.createRange();
      range.selectNodeContents(el);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }

    function onClick(e: MouseEvent) {
      const t = e.target as HTMLElement | null;
      if (!t) return;

      if (mode === "ask") {
        if (!hovered) return;
        e.preventDefault();
        e.stopPropagation();
        post({
          type: "editor:pick",
          tag: hovered.tagName.toLowerCase(),
          text: (hovered.textContent || "").trim().slice(0, 160),
          nearestHeading: nearestHeading(hovered),
          region: regionOf(hovered),
          url: window.location.href,
        });
        return;
      }

      // inline mode: editing is started only via the hover "Edit" button, so a
      // normal click does nothing special (links still navigate the preview).
      // If an edit is in progress, clicking elsewhere commits it (focusout also
      // handles this).
      if (editing && t !== editing) commitEdit();
    }

    function onKeyDown(e: KeyboardEvent) {
      if (!editing) return;
      if (e.key === "Enter") {
        e.preventDefault();
        commitEdit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancelEdit();
      }
    }

    // Commit when focus leaves the element being edited (clicking the chat,
    // switching tabs, etc.) so an in-progress edit is never silently lost.
    function onFocusOut(e: FocusEvent) {
      if (editing && e.target === editing) commitEdit();
    }

    function onParentMessage(ev: MessageEvent) {
      if (ev.source !== window.parent) return; // only trust the hosting editor
      parentOrigin = ev.origin;
      const d = ev.data as { type?: string; mode?: Mode };
      if (d?.type === "editor:set-mode" && (d.mode === "inline" || d.mode === "ask")) {
        if (editing) commitEdit();
        mode = d.mode;
        clearHover();
        hideEditButton();
        hideBannerButton();
        lastMoveTarget = null;
      }
      // Parent's confirm dialog was cancelled — restore the original text.
      if (d?.type === "editor:revert-text") {
        if (pendingEl) pendingEl.textContent = pendingOriginal;
        pendingEl = null;
        pendingOriginal = "";
      }
      // Save confirmed — nothing to undo; the new text already shows.
      if (d?.type === "editor:confirm-text") {
        pendingEl = null;
        pendingOriginal = "";
      }
    }

    // The button is positioned in viewport coords, so hide it on scroll/resize
    // rather than let it drift away from its element.
    const onScrollOrResize = () => {
      hideEditButton();
      hideBannerButton();
      lastMoveTarget = null; // force a recompute on the next move
    };

    document.addEventListener("mousemove", onMove, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("focusout", onFocusOut, true);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("message", onParentMessage);
    // Tell the parent we're ready and which mode we defaulted to.
    window.parent.postMessage({ type: "editor:ready", mode }, "*");

    return () => {
      document.removeEventListener("mousemove", onMove, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("focusout", onFocusOut, true);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("message", onParentMessage);
      clearHover();
      editBtn.remove();
      bannerBtn.remove();
    };
  }, []);

  return null;
}
